//TBS/frontend/src/App.jsx
import React, { Suspense, lazy, useEffect, useState } from "react";
import {
  Router,
  Routes,
  Route,
  useParams,
  useLocation
} from "react-router-dom";
import API from "./helpers/API";
import { useAuth } from "./Context/AuthContext";
import { ToastContainer, toast } from "react-toastify";
import styled from "styled-components";
import { Helmet } from "react-helmet";
import Loader from "./ui/Loader/Loader";
import $ from "jquery";
import "./Pages/ApplyJobApplications/ApplyJobApplicationModal.css";
import "./App.css";

import { AdminRoute } from "./routes/AdminRoute";
import { ApplicantRoute } from "./routes/ApplicantRoute";

import { EmployerRoute } from "./routes/EmployerRoute";
import EmployerDashboard from "./Pages/Employer/Dashboard";

// Import LinkedIn reminder toast function (moved to top to satisfy ESLint)
import { showLinkedInReminderToast } from "./components/LinkedInReminderToast";

const Home = lazy(() => import("./Pages/Home"));
const JobSearchInterface = lazy(
  () => import(/* webpackPrefetch: true */ "./Pages/JobSearchInterface")
);
const JobDescription = lazy(
  () => import("./Pages/JobDescription/JobDescription")
);
const Login = lazy(() => import("./Pages/Login/Login"));
const Register = lazy(() => import("./Pages/Register/Register"));
const AdminDashboardHome = lazy(() => import("./Pages/Admin/Home"));
const ManageJobs = lazy(() => import("./Pages/Admin/Jobs/ManageJobs"));
const AddTestJob = lazy(() => import("./Pages/Admin/Jobs/AddTestJob"));
const ManageAdminJobTestQualifications = lazy(
  () => import("./Pages/Admin/Qualifications/ManageAdminJobTestQualifications")
);
const AddTestQualification = lazy(
  () => import("./Pages/Admin/Qualifications/AddTestQualification")
);
const ManageAdminTestWorkExperiences = lazy(
  () => import("./Pages/Admin/WorkExperiences/ManageAdminTestWorkExperiences")
);
const AddTestWorkExperience = lazy(
  () => import("./Pages/Admin/WorkExperiences/AddTestWorkExperience")
);
const ManageAdminTestWorkModes = lazy(
  () => import("./Pages/Admin/WorkModes/ManageAdminTestWorkModes")
);
const AddTestWorkMode = lazy(
  () => import("./Pages/Admin/WorkModes/AddTestWorkMode")
);
const AddTestCountry = lazy(
  () => import("./Pages/Admin/Countries/AddTestCountry")
);
const ManageAdminJobTestCountries = lazy(
  () => import("./Pages/Admin/Countries/ManageAdminTestCountries")
);
const AddTestProvince = lazy(
  () => import("./Pages/Admin/Provinces/AddTestProvince")
);
const ManageAdminTestProvinces = lazy(
  () => import("./Pages/Admin/Provinces/ManageAdminTestProvinces")
);
const AddSector = lazy(() => import("./Pages/Admin/Sectors/AddSector"));
const ManageSectors = lazy(() => import("./Pages/Admin/Sectors/ManageSectors"));
const AddTestSalary = lazy(
  () => import("./Pages/Admin/Salaries/AddTestSalary")
);
const ManageAdminTestSalaries = lazy(
  () => import("./Pages/Admin/Salaries/ManageAdminTestSalaries")
);
const AllContactUsMessages = lazy(
  () => import("./Pages/Admin/ContactMessages/AllContactUsMessages")
);
const TestViewContactMessage = lazy(
  () => import("./Pages/Admin/ContactMessages/TestViewContactMessage")
);
const TestAdminSubscribers = lazy(
  () => import("./Pages/Admin/Subscribers/TestAdminSubscribers")
);
const ManageAdminTestWorkAuthorizations = lazy(
  () =>
    import("./Pages/Admin/WorkAuthorizations/ManageAdminTestWorkAuthorizations")
);
const AddTestWorkAuthorization = lazy(
  () => import("./Pages/Admin/WorkAuthorizations/AddTestWorkAuthorization")
);
const AdminTestChangePassword = lazy(
  () => import("./Pages/Admin/Account/AdminTestChangePassword")
);

// NEW: Hiring Client's Logos admin screens
const AddHiringClientLogo = lazy(
  () => import("./Pages/Admin/HiringClientLogos/AddHiringClientLogo")
);
const ManageHiringClientLogos = lazy(
  () => import("./Pages/Admin/HiringClientLogos/ManageHiringClientLogos")
);

const RegisteredUsers = lazy(() => import("./Pages/Admin/RegisteredUsers"));

const AboutUs = lazy(() => import("./Pages/About/AboutUs"));
const WhoWeAre = lazy(() => import("./Pages/About/WhoWeAre"));
const OurLocations = lazy(() => import("./Pages/About/OurLocations"));
const ContactUs = lazy(() => import("./Pages/Contact/ContactUs"));
const ThankYou = lazy(() => import("./Pages/Contact/TestThankYou"));
const ITRecruitment = lazy(
  () => import("./Pages/Business/ITRecruitment/ITRecruitment")
);
const GovtMinistryRecruitment = lazy(
  () => import("./Pages/Business/GovtMinistry/GovtMinistryRecruitment")
);
const HealthCareMedicalRecruitment = lazy(
  () =>
    import(
      "./Pages/Business/HealthcareMedicalRecruitment/HealthcareMedicalRecruitment"
    )
);
const ManufacturingRecruitment = lazy(
  () =>
    import("./Pages/Business/ManufacturingRecruitment/ManufacturingRecruitment")
);
const AdminOfficeSupportRecruitment = lazy(
  () =>
    import(
      "./Pages/Business/AdminOfficeSupportRecruitment/AdminOfficeSupportRecruitment"
    )
);
const TransportLogisticsRecruitment = lazy(
  () =>
    import(
      "./Pages/Business/TransportLogisticsRecruitment/TransportLogisticsRecruitment"
    )
);
const ConstructionInfrastructureRecruitment = lazy(
  () =>
    import(
      "./Pages/Business/ConstructionInfrastructureRecruitment/ConstructionInfrastructureRecruitment"
    )
);
const EducationRecruitment = lazy(
  () => import("./Pages/Education/EducationRecruitment")
);
const PermanentRecruitment = lazy(
  () => import("./Pages/Employers/PermanentRecruitment/PermanentRecruitment")
);
const TailoredSolutionsForEmployers = lazy(
  () =>
    import(
      "./Pages/Employers/TailoredSolutionsForEmployers/TailoredSolutionsForEmployers"
    )
);
const FluidWorkforce = lazy(
  () => import("./Pages/Employers/FluidWorkforce/FluidWorkforce")
);

const BehavioralHealthSolutions = lazy(
  () => import("./Pages/Business/HealthSolutions/BehavioralHealthSolutions")
);
const HealthBillingCollectionsSolutions = lazy(
  () =>
    import("./Pages/Business/HealthSolutions/HealthBillingCollectionsSolutions")
);
const BehavioralHealthCredentialingSolutions = lazy(
  () =>
    import(
      "./Pages/Business/HealthSolutions/BehavioralHealthCredentialingSolutions"
    )
);
const HealthcareMergersAcquisitionsSolutions = lazy(
  () =>
    import(
      "./Pages/Business/HealthSolutions/HealthcareMergersAcquisitionsSolutions"
    )
);
const ThinkBeyondPrivacyPolicy = lazy(
  () => import("./Pages/ThinkBeyondPrivacyPolicy/ThinkBeyondPrivacyPolicy")
);

const EmailVerification = lazy(
  () => import("./Pages/EmailConfirmation/PageEmailVerification")
);
const ForgotPassword = lazy(
  () => import("./Pages/PasswordActions/ForgotPassword")
);
const PasswordResetLink = lazy(
  () => import("./Pages/PasswordActions/PasswordResetLink")
);
const PageChangePassword = lazy(
  () => import("./Pages/PasswordActions/ChangePassword")
);

// *************************** Pages Applicant Dashboard Start *********************************************
const TestApplicantDashboard = lazy(
  () => import("./Pages/Applicant/ApplicantHome")
);
const PageProfile = lazy(() => import("./Pages/Applicant/Profile/Profile"));
const ApplicantResumeUpload = lazy(
  () => import("./Pages/ResumesCV/ApplicantResumeUpload")
);
// *************************** Pages Applicant Dashboard End ***********************************************

// const AboutUs = lazy(() => import("./features/About/AboutUs"));
const OurMission = lazy(() => import("./features/OurMission/OurMission"));
const OurVision = lazy(() => import("./features/OurVision/OurVision"));
const OurCoreServices = lazy(
  () => import("./features/TBSCoreServices/OurCoreServices")
);
const ITInfrastructure = lazy(
  () => import("./features/ITInfrastructure/ITInfrastructure")
);
const BehavioralHealth = lazy(
  () => import("./features/BehavioralHealth/BehavioralHealth")
);
const Education = lazy(() => import("./features/Education/Education"));
const HealthBillingCollection = lazy(
  () => import("./features/HealthBillingCollections/HealthBillingCollections")
);
const HealthcareMergersAndAcquisitions = lazy(
  () => import("./features/M&A/HealthcareM&A")
);
const RecruitingAndLeadership = lazy(
  () => import("./features/Recruiting&Leadership/Recruiting&Leadership")
);
const BehavioralHealthCredentialingAndContracting = lazy(
  () =>
    import(
      "./features/BehavioralHealthCredentialing&Contracting/BehavioralHealthCredentialing&Contracting"
    )
);
const WebDevelopmentServices = lazy(
  () => import("./features/WebDevelopmentServices/WebDevelopmentServices")
);
const DataAnalytics = lazy(
  () => import("./features/DataAnalytics/DataAnalytics")
);
const HigherEducation = lazy(
  () => import("./features/HigherEducation/HigherEducation")
);
const EducationCounselling = lazy(
  () => import("./features/EducationCounselling/EducationCounselling")
);
const Infrastructure = lazy(
  () => import("./features/InfrastructureServices/Infrastructure")
);
const Employers = lazy(() => import("./features/Employers/Employers"));
const ApplyJobForm = lazy(() => import("./features/Account/ApplyJobForm"));
const ApplyNonITJobForm = lazy(
  () => import("./features/Account/ApplyNonITJobForm")
);
// const BrowseJobs = lazy(() => import("./features/Jobs/BrowseJobs"));
const NonITJobs = lazy(() => import("./features/Jobs/NonITJobs"));
const JobDetails = lazy(() => import("./features/Jobs/JobDetails"));
const NonITJobDetails = lazy(() => import("./features/Jobs/NonItJobDetails"));
const SearchJobFilter = lazy(() => import("./features/Jobs/SearchJobFilter"));

//Privacy Policy
const PrivacyPlicy = lazy(
  () => import("./features/PrivacyPolicy/PrivacyPolicy")
);

const ThinkBeyondTermsAndConditions = lazy(
  () =>
    import(
      "./Pages/ThinkBeyondTermsAndConditions/ThinkBeyondTermsAndConditions"
    )
);

const RecruitmentProcess = lazy(
  () => import("./features/Home/Recruitment-Process-Outsourcing")
);

const SoftwareDevelopment = lazy(
  () => import("./features/Home/Software-Development")
);

const CandidateSourcing = lazy(
  () => import("./features/Home/Candidate-Sourcing")
);

// const AdminDashboardHome = lazy(() => import("./admin/Home"));
const AddCountry = lazy(() => import("./admin/Countries/AddCountry"));
const ManageCountries = lazy(() => import("./admin/Countries/ManageCountries"));
const AddSalary = lazy(() => import("./admin/Salaries/AddSalary"));
const ManageSalaries = lazy(() => import("./admin/Salaries/ManageSalaries"));
const AddProvince = lazy(() => import("./admin/Provinces/AddProvince"));
const ManageProvinces = lazy(() => import("./admin/Provinces/ManageProvinces"));
// const AddSector = lazy(() => import("./admin/Sectors/AddSector"));

const ManageProducts = lazy(() => import("./admin/Products/ManageProducts"));
const ManageProductCategories = lazy(
  () => import("./admin/ProductCategory/ManageProductCategories")
);

const AddQualification = lazy(
  () => import("./admin/Qualifications/AddQualification")
);
const ManageQualifications = lazy(
  () => import("./admin/Qualifications/ManageQualifications")
);
const AddWorkExperience = lazy(
  () => import("./admin/WorkExperiences/AddWorkExperience")
);
const ManageWorkExperiences = lazy(
  () => import("./admin/WorkExperiences/ManageWorkExperiences")
);
const AddWorkMode = lazy(() => import("./admin/WorkModes/AddWorkMode"));
const ManageWorkModes = lazy(() => import("./admin/WorkModes/ManageWorkModes"));
const AddWorkAuthorization = lazy(
  () => import("./admin/WorkAuthorizations/AddWorkAuthorization")
);
const ManageWorkAuthorizations = lazy(
  () => import("./admin/WorkAuthorizations/ManageWorkAuthorizations")
);
const PostJob = lazy(() => import("./admin/Jobs/PostJob"));
const PostNonITJob = lazy(() => import("./admin/Jobs/PostNonITJob"));
// const ManageJobs = lazy(() => import("./admin/Jobs/ManageJobs"));
const ManageNonITJobs = lazy(() => import("./admin/Jobs/ManageNonITJobs"));
const DeleteJob = lazy(() => import("./admin/Jobs/DeleteJob"));
const DeleteNonITJob = lazy(() => import("./admin/Jobs/DeleteNonITJob"));
const UpdateJob = lazy(() => import("./admin/Jobs/UpdateJob"));
const UpdateNonITJob = lazy(() => import("./admin/Jobs/UpdateNonITJob"));
const ViewJobDetails = lazy(() => import("./admin//Jobs/ViewJobDetails"));
const ViewNonITJobDetails = lazy(
  () => import("./admin/Jobs/ViewNonITJobDetails")
);

const ManageContactMessages = lazy(
  () => import("./admin/ContactMessages/ManageContactMessages")
);
const ViewContactMessage = lazy(
  () => import("./admin/ContactMessages/ViewContactMessage")
);

const DeleteContactMessage = lazy(
  () => import("./admin//ContactMessages/DeleteContactMessage")
);

const AdminChangePassword = lazy(
  () => import("./admin/ChangePassword/ChangePassword")
);

const Subscribers = lazy(() => import("./admin/Subscribers/Subscribers"));

// const ApplicantDashboardHome = lazy(() => import("./applicant/Home"));
// const ApplicantProfile = lazy(
//   () => import("./applicant/ApplicantProfiles/ApplicantProfile")
// );
const ApplicantAppliedJobs = lazy(
  () => import("./applicant/Jobs/ApplicantAppliedJobs")
);
const ApplicantChangePassword = lazy(
  () => import("./applicant/ChangePassword/ChangePassword")
);
const DeleteEducation = lazy(
  () => import("./applicant//Education/DeleteEducation")
);
const DeleteApplicantExperience = lazy(
  () => import("./applicant/Experience/DeleteApplicatExperience")
);
const DeleteSkill = lazy(() => import("./applicant/Skill/DeleteSkill"));

// Delete User Resume from Applicant Dashboard
const DeleteResume = lazy(() => import("./applicant/Resume/DeleteResume"));

// UnAuthorized Page
const UnAuthorized = lazy(() => import("./features/UnAuthorized/UnAuthorized"));

// Unsubscribe
// const Unsubscribe = lazy(() => import("./features/Unsubscribe/Unsubscribe"));
const Unsubscribe = lazy(() => import("./Pages/Unsubscribe/Unsubscribe"));

const UsersSubscriptionFormDetails = lazy(
  () => import("./Pages/UsersSubscription/UsersSubscription")
);

const ManualUnsubscribeEntry = lazy(
  () => import("./Pages/Admin/Subscribers/ManualUnsubscribeEntry")
);

const AddSubscriberToMasterFile = lazy(
  () => import("./Pages/Admin/Subscribers/AddSubscriberToMasterFile")
);

const Shop = lazy(() => import("./features/Shop/Shop"));
const ProductDetail = lazy(() => import("./features/Shop/ProductDetail"));
const Cart = lazy(() => import("./features/Shop/Cart"));

// Customer Orders
const Orders = lazy(() => import("./applicant/CustomerOrders/Orders"));

const LinkedInCallback = lazy(
  () => import("./components/LinkedIn/LinkedInCallback")
);
const AuthSuccess = lazy(() => import("./components/LinkedIn/AuthSuccess"));
const ApplicantsByJob = lazy(
  () => import("./Pages/Admin/ApplicantsByJob/ApplicantsByJob")
);

const StyledContainer = styled(ToastContainer)`
  &&&.Toastify__toast-container {
  }
  .Toastify__toast {
    width: 550px !important;
    height: 200px;
    font-family: Trebuchet-ms;
    font-weight: bold;
  }
  .Toastify__toast-body {
    width: 550px !important;
    height: 200px;
    font-family: Trebuchet-ms;
  }
  .Toastify__progress-bar {
  }
`;

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function App() {
  const [auth, setAuth] = useAuth();
  const [user, setUser] = useState({});
  const params = useParams();

  const fetchRegUserById = async () => {
    try {
      const { data } = await API.get(`/users/fetchRegUserById/${params._id}`);
      setUser(data.user);
    } catch (error) {
      console.log(error);
    }
  };
  useEffect(() => {
    if (params?._id) fetchRegUserById();
  }, [params?._id]);

  // ----------------------------------------------------------------------
  // LinkedIn Reminder Toast Integration
  // ----------------------------------------------------------------------
  useEffect(() => {
    // Wait until auth is fully loaded and user object exists
    if (!auth || !auth.user) return;

    const currentUser = auth.user;
    // Only show for applicants (role === 2) with empty LinkedIn URL
    if (currentUser.role != 2) return;

    const linkedin =
      currentUser.linkedin_url ||
      currentUser.linkedInUrl ||
      currentUser.LinkedInUrl ||
      currentUser.LinkedInURL;
    if (linkedin && linkedin.trim() !== "") return;

    // Show toast (the function internally checks sessionStorage to avoid duplicates)
    showLinkedInReminderToast(currentUser, setAuth);
  }, [auth, setAuth]);
  // ----------------------------------------------------------------------

  return (
    <>
      <ScrollToTop />
      <Suspense fallback={<Loader />}>
        <Routes>
          {/* <Route path="/" element={<Home />} /> */}
          <Route path="/" element={<Home />} />
          <Route path="/Search-Jobs" element={<JobSearchInterface />} />
          <Route
            path="/Job-Description/:jobId/:slug"
            element={<JobDescription />}
          />
          <Route path="/Login" element={<Login />} />
          <Route path="/Register" element={<Register />} />
          {/* <Route path="/Admin/Test-Admin-Dashboard" element={<TestDashboardHome />} /> */}
          {/* <Route path="/Admin/Test-Manage-Jobs" element={<ManageTestJobs />} /> */}
          <Route path="About-Us" element={<AboutUs />} />
          <Route path="Who-We-Are" element={<WhoWeAre />} />
          <Route path="Our-Locations" element={<OurLocations />} />
          <Route path="/Contact-Us" element={<ContactUs />} />
          <Route path="/Thank-You" element={<ThankYou />} />

          <Route
            path="/Information-Technology-Recruitment"
            element={<ITRecruitment />}
          />
          <Route
            path="/Government-Ministry-Recruitment"
            element={<GovtMinistryRecruitment />}
          />
          <Route
            path="/Healthcare-&-Medical-Recruitment"
            element={<HealthCareMedicalRecruitment />}
          />
          <Route
            path="/Manufacturing-Recruitment"
            element={<ManufacturingRecruitment />}
          />
          <Route
            path="/Admin-&-Office-Support-Recruitment"
            element={<AdminOfficeSupportRecruitment />}
          />
          <Route
            path="/Transport-&-Logistics-Recruitment"
            element={<TransportLogisticsRecruitment />}
          />
          <Route
            path="/Construction-&-Infrastructure-Recruitment"
            element={<ConstructionInfrastructureRecruitment />}
          />
          <Route
            path="/Education-Recruitment"
            element={<EducationRecruitment />}
          />
          <Route
            path="/Permanent-Hiring-Solutions"
            element={<PermanentRecruitment />}
          />
          <Route
            path="/Tailored-Solutions-For-Employers"
            element={<TailoredSolutionsForEmployers />}
          />
          <Route
            path="/Fluid-Workforce-Solutions"
            element={<FluidWorkforce />}
          />
          <Route
            path="/Behavioral-Health-Solutions"
            element={<BehavioralHealthSolutions />}
          />
          <Route
            path="/Health-Billing-&-Collections-Solutions"
            element={<HealthBillingCollectionsSolutions />}
          />
          <Route
            path="/Behavioral-Health-Credentialing-&-Contracting-Solutions"
            element={<BehavioralHealthCredentialingSolutions />}
          />
          <Route
            path="/Healthcare-Mergers-&-Acquisitions-Solutions"
            element={<HealthcareMergersAcquisitionsSolutions />}
          />
          <Route
            path="/ThinkBeyond-Privacy-Policy"
            element={<ThinkBeyondPrivacyPolicy />}
          />

          {/* <Route path="/verify-account/:id/:token" element={<PageEmailVerification />} /> */}
          <Route
            path="/Email-Account-Verification/:id/:token"
            element={<EmailVerification />}
          />
          <Route
            path="/Forgot-Password-Reset-Request"
            element={<ForgotPassword />}
          />
          <Route
            path="/Password-Reset-Link/:id/:resetToken"
            element={<PasswordResetLink />}
          />

          <Route
            path="/Recruitment-Process-Outsourcing"
            element={<RecruitmentProcess />}
          />
          <Route
            path="/Software-Development-Services"
            element={<SoftwareDevelopment />}
          />
          <Route path="/Candidate-Sourcing" element={<CandidateSourcing />} />
          {/* <Route path="/About-Us" element={<AboutUs />} /> */}
          <Route path="/About-Us/Our-Mission" element={<OurMission />} />
          <Route path="/About-Us/Our-Vision" element={<OurVision />} />
          <Route path="/Services" element={<OurCoreServices />} />
          <Route
            path="/Services/IT-Infrastructure"
            element={<ITInfrastructure />}
          />
          <Route path="/Behavioral-Health" element={<BehavioralHealth />} />
          <Route path="/Education" element={<Education />} />
          <Route
            path="/Health-Billing-&-Collections"
            element={<HealthBillingCollection />}
          />
          <Route
            path="/Healthcare-M-&-A"
            element={<HealthcareMergersAndAcquisitions />}
          />
          <Route
            path="/Recruiting-&-Leadership"
            element={<RecruitingAndLeadership />}
          />
          <Route
            path="/Behavioral-Health-Credentialing-&-Contracting"
            element={<BehavioralHealthCredentialingAndContracting />}
          />
          <Route
            path="/Web-Development-Services"
            element={<WebDevelopmentServices />}
          />
          <Route path="/Data-Analytics" element={<DataAnalytics />} />
          <Route path="/Higher-Education" element={<HigherEducation />} />
          <Route
            path="/Education-Counselling"
            element={<EducationCounselling />}
          />
          <Route path="/Infrastructure" element={<Infrastructure />} />
          <Route path="/Employers" element={<Employers />} />

          {/* <Route path="/Search-Jobs" element={<BrowseJobs />} /> */}
          <Route path="/Non-IT-Jobs" element={<NonITJobs />} />
          <Route path="/Search" element={<SearchJobFilter />} />
          <Route path="/Job-Details/:slug" exact element={<JobDetails />} />
          <Route
            path="/Non-IT-Job-Details/:slug"
            exact
            element={<NonITJobDetails />}
          />

          {/* <Route path="/Contact-Us" element={<ContactUs />} /> */}
          {/* <Route path="/Thank-You-For-Contacting-Us" element={<ThankYou />} /> */}

          {/* <Route path="/Login" element={<Login />} /> */}
          {/* <Route path="/Register" element={<Register />} /> */}
          {/* <Route
            path="/user-verify-account/:id/:token"
            element={<EmailVerification />}
          /> */}
          {/* <Route path="/Account/Forgot-Password" element={<ForgotPassword />} /> */}
          {/* <Route
            path="/reset-password/:id/:resetToken"
            element={<ResetPassword />}
          /> */}

          <Route
            path="/User-Subscription-Form"
            element={<UsersSubscriptionFormDetails />}
          />
          <Route path="/linkedin-callback" element={<LinkedInCallback />} />
          {/* <Route path="/auth/linkedin/callback" element={<LinkedInCallback />} />
          <Route path="/auth/success" element={<AuthSuccess />} /> */}
          <Route path="/auth/success" element={<AuthSuccess />} />

          <Route path="/" element={<AdminRoute />}>
            {/* <Route path="/Admin/Dashboard" element={<AdminDashboardHome />} /> */}
            <Route path="/Admin/Dashboard" element={<AdminDashboardHome />} />
            {/* // AFTER */}
            {/* <Route path="/Admin/Dashboard" element={<AdminDashboardHome userId={auth?.user?.userId} />} /> */}

            <Route
              path="/Admin/Countries/Add-Country"
              element={<AddCountry />}
            />

            <Route path="/Admin/Add-Job" element={<AddTestJob />} />
            <Route path="/Admin/Manage-Jobs" element={<ManageJobs />} />
            {/* <Route
              path="/Admin/Test-Manage-Jobs"
              element={<ManageTestJobs userId={auth?.user?.userId} />}
            /> */}
            <Route
              path="/Admin/Manage-Qualifications"
              element={<ManageAdminJobTestQualifications />}
            />
            <Route
              path="/Admin/Add-Qualification"
              element={<AddTestQualification />}
            />
            <Route
              path="/Admin/Manage-Work-Experiences"
              element={<ManageAdminTestWorkExperiences />}
            />
            <Route
              path="/Admin/Add-Work-Experience"
              element={<AddTestWorkExperience />}
            />
            <Route
              path="/Admin/Manage-Work-Modes"
              element={<ManageAdminTestWorkModes />}
            />
            <Route path="/Admin/Add-Work-Mode" element={<AddTestWorkMode />} />
            <Route path="/Admin/Add-Country" element={<AddTestCountry />} />
            <Route
              path="/Admin/Manage-Countries"
              element={<ManageAdminJobTestCountries />}
            />
            <Route path="/Admin/Add-Province" element={<AddTestProvince />} />
            <Route
              path="/Admin/Manage-Provinces"
              element={<ManageAdminTestProvinces />}
            />
            <Route path="/Admin/Add-Sector" element={<AddSector />} />
            <Route path="/Admin/Manage-Sectors" element={<ManageSectors />} />
            <Route path="/Admin/Add-Salary" element={<AddTestSalary />} />
            <Route
              path="/Admin/Manage-salaries"
              element={<ManageAdminTestSalaries />}
            />

            {/* NEW: Hiring Client's Logos admin routes */}
            <Route
              path="/Admin/Add-Hiring-Client-Logo"
              element={<AddHiringClientLogo />}
            />
            <Route
              path="/Admin/Manage-Hiring-Client-Logos"
              element={<ManageHiringClientLogos />}
            />

            <Route
              path="/Admin/All-Contact-Us-Messages"
              element={<AllContactUsMessages />}
            />
            <Route
              path="/Admin/View-Contact-Message/:id"
              element={<TestViewContactMessage />}
            />
            <Route
              path="/Admin/Email-Subscribers"
              element={<TestAdminSubscribers />}
            />

            <Route
              path="/Admin/Add-Work-Authorization"
              element={<AddTestWorkAuthorization />}
            />
            <Route
              path="/Admin/Manage-Work-Authorizations"
              element={<ManageAdminTestWorkAuthorizations />}
            />
            <Route
              path="/Admin/Change-Password"
              element={<AdminTestChangePassword />}
            />

            <Route
              path="/Admin/Countries/Manage-Countries"
              element={<ManageCountries />}
            />
            <Route path="/Admin/Salaries/Add-Salary" element={<AddSalary />} />
            <Route
              path="/Admin/Salaries/Manage-Salaries"
              element={<ManageSalaries />}
            />
            <Route
              path="/Admin/Provinces/Add-Province"
              element={<AddProvince />}
            />
            <Route
              path="/Admin/Provinces/Manage-Provinces"
              element={<ManageProvinces />}
            />
            <Route path="/Admin/Sectors/Add-Sector" element={<AddSector />} />
            <Route
              path="/Admin/Sectors/Manage-Sectors"
              element={<ManageSectors />}
            />
            <Route
              path="/Admin/Products/Manage-Products"
              element={<ManageProducts />}
            />
            <Route
              path="/Admin/ProductCategories/Manage-Product-Categories"
              element={<ManageProductCategories />}
            />
            <Route
              path="/Admin/Qualifications/Add-Qualification"
              element={<AddQualification />}
            />
            <Route
              path="/Admin/Qualifications/Manage-Qualifications"
              element={<ManageQualifications />}
            />
            <Route
              path="/Admin/WorkExperiences/Add-Work-Experience"
              element={<AddWorkExperience />}
            />
            <Route
              path="/Admin/WorkExperiences/Manage-Work-Experiences"
              element={<ManageWorkExperiences />}
            />
            <Route
              path="/Admin/WorkModes/Add-Work-Mode"
              element={<AddWorkMode />}
            />
            <Route
              path="/Admin/WorkModes/Manage-Work-Modes"
              element={<ManageWorkModes />}
            />
            <Route
              path="/Admin/WorkAuthorizations/Add-Work-Authorization"
              element={<AddWorkAuthorization />}
            />
            <Route
              path="/Admin/WorkAuthorizations/Manage-Work-Authorizations"
              element={<ManageWorkAuthorizations />}
            />
            <Route path="/Admin/Jobs/Post-Job" element={<PostJob />} />
            <Route
              path="/Admin/Jobs/Post-Non-IT-Job"
              element={<PostNonITJob />}
            />
            <Route path="/Admin/Jobs/Manage-Jobs" element={<ManageJobs />} />
            <Route
              path="/Admin/Jobs/Manage-Non-IT-Jobs"
              element={<ManageNonITJobs />}
            />
            <Route
              path="/Admin/Jobs/DeleteJob/:slug"
              element={<DeleteNonITJob />}
            />
            <Route
              path="/Admin/Jobs/Delete-Job/:slug"
              element={<DeleteJob />}
            />
            <Route
              path="/Admin/Jobs/Update-Job/:slug"
              element={<UpdateJob />}
            />

            <Route
              path="/Admin/Jobs/View-Job/:slug"
              element={<ViewJobDetails />}
            />
            <Route
              path="/Admin/Jobs/ViewJob/:slug"
              element={<ViewNonITJobDetails />}
            />
            <Route
              path="/Admin/Jobs/UpdateJob/:slug"
              element={<UpdateNonITJob />}
            />
            <Route
              path="/Admin/Contact-Messages/Manage-Contact-Us-Messages"
              element={<ManageContactMessages />}
            />
            <Route
              path="/Admin/Contact-Messages/View-Message/:id"
              element={<ViewContactMessage />}
            />
            <Route
              path="/Admin/Contact-Messages/Delete-Message/:id"
              element={<DeleteContactMessage />}
            />
            <Route
              path="/Account/Change-Password"
              element={<AdminChangePassword />}
            />
            <Route path="/Admin/Subscribers" element={<Subscribers />} />
            <Route
              path="/Admin/Applicants-By-Job"
              element={<ApplicantsByJob />}
            />
            <Route
              path="/Admin/Registered-Users"
              element={<RegisteredUsers />}
            />
            <Route
              path="/Admin/Manual-Unsubscribe"
              element={<ManualUnsubscribeEntry />}
            />
            <Route
              path="/Admin/Add-To-Master-File"
              element={<AddSubscriberToMasterFile />}
            />
          </Route>
          <Route path="/" element={<EmployerRoute />}>
            <Route path="/Employer/Dashboard" element={<EmployerDashboard />} />
          </Route>

          <Route path="/" element={<ApplicantRoute />}>
            {/* <Route
              path="/Applicant/Dashboard"
              element={<ApplicantDashboardHome />}
            /> */}
            {/* %%%%%%%%%%%%%%%%%%%%%%%% Applicant Routes Starts %%%%%%%%%%%%%%%%%%%%%%%%%% */}
            <Route
              path="/Applicant-Profile-Dashboard"
              element={<TestApplicantDashboard />}
            />
            <Route
              path={`/Profile/${auth?.user?.userId}`}
              element={<PageProfile />}
            />
            <Route
              path={`/Applicant-Resume-Upload/${auth?.user?.userId}`}
              element={<ApplicantResumeUpload />}
            />
            <Route
              path="/User-Change-Password"
              element={<PageChangePassword />}
            />
            {/* %%%%%%%%%%%%%%%%%%%%%%%% Applicant Routes Ends % %%%%%%%%%%%%%%%%%%%%%%%%%% */}
            <Route path="/Account/Apply-Job/:slug" element={<ApplyJobForm />} />
            <Route
              path="/Account/Apply-Non-IT-Job/:slug"
              element={<ApplyNonITJobForm />}
            />
            {/* <Route
              path={`/Applicant/Profile/${auth?.user?.userId}`}
              element={<ApplicantProfile />}
            /> */}
            <Route
              path="/Applicant/Jobs/Applied-Jobs"
              element={<ApplicantAppliedJobs />}
            />
            <Route
              path="/Applicant/Change-Password"
              element={<ApplicantChangePassword />}
            />
            {/* <Route
              path={`/Resume-Upload/${auth?.user?.userId}`}
              element={<ResumeUpload />}
            /> */}
            <Route
              path="/Applicant/Delete-Education/:id"
              element={<DeleteEducation />}
            />
            <Route
              path="/Applicant/Delete-Applicant-Experience/:id"
              element={<DeleteApplicantExperience />}
            />
            <Route
              path="/Applicant/Delete-Skill/:id"
              element={<DeleteSkill />}
            />
            <Route
              path="/Applicant/Delete-Resume/:id"
              element={<DeleteResume />}
            />
            <Route path="/Orders" element={<Orders />} />
            {/* <Route path="/oauth-success" element={<OAuthSuccess />} /> */}
          </Route>
          <Route path="/Thinkbeyond-Access-Denied" element={<UnAuthorized />} />
          <Route path="/Unsubscribe" element={<Unsubscribe />} />
          <Route path="/Online-Shop" element={<Shop />} />
          <Route path="/Product-Detail/:slug" element={<ProductDetail />} />
          <Route path="/Cart-Details" element={<Cart />} />
          <Route path="Privacy-Policy" element={<PrivacyPlicy />} />
          <Route
            path="/ThinkBeyond-Terms-And-Conditions"
            element={<ThinkBeyondTermsAndConditions />}
          />
        </Routes>
      </Suspense>
      <StyledContainer />
    </>
  );
}

export default App;
